// src/components/ChargingStation/useChargingStation.ts
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Platform, InteractionManager } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { AppDispatch, RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMyDistanceFromCharger,
  getMyDrivingTime,
  updateSelectedCharger,
} from '../store/actions/charging-actions';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getUserLocation } from '../components/common/utils/get-user-location';
import { getLocationFromIp } from '../components/common/utils/ip-address';
import { getDriverPlan } from '../store/actions/user-actions';
import {
  deleteSchedule,
  SendSchedule,
  setSchedule,
} from '../store/actions/scheduling-actions';

import { ChargerAttributes } from '../models/charger-model';
import { OrderAttributes } from '../models/order-model';
import { UserSessionAttributes } from '../models/user-session-model';
import { ActiveChargeAttributes } from '../models/active-charges-model';
import { isDiscountEffectivelyFree } from '../components/common/utils/get-price-info';

import { useStripeWeb } from '../components/ChargingStation/hooks/useStripeWeb.web';
// Only import mobile hook on native platforms
// const useStripeMobile =
//   Platform.OS === 'web'
//     ? null
//     : require('../components/ChargingStation/hooks/useStripeMobile.native')
//         .useStripeMobile;
//@ts-ignore
import { useStripeMobile } from '../components/ChargingStation/hooks/useStripeMobile';
import { notificationService } from '../services/notification-service';
import { cancelOrder, updateOrderEmail } from '../services/orders-service';
import { checkoutCharger } from '../services/charger-service';
import { isSuccessful } from '../services/api-requests';
import { isCurrentlyUnavailable, getScheduleConflicts } from '../components/common/utils/unavailable-times-utils';
import { isOcppCharger } from '../ocpp/utils/chargerOcppCapabilities';
import * as Location from 'expo-location';
import RoleService from '../services/role-service';
import { BottomSheetModalRef } from '../components/BottomSheet';

/**
 * Main hook for ChargerStation screen.
 * All shared state & side-effects live here.
 * Stripe logic is delegated to useStripeWeb (web) or useStripeMobile (native).
 */
export function useChargingStation(navigation: any, route: any) {
  // ────────────────────────────────────────────────────────────────────────────────
  // Redux & session state
  // ────────────────────────────────────────────────────────────────────────────────
  const dispatch: AppDispatch = useDispatch();
  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user.activeCharges
  );
  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );
  const selectedCharger: ChargerAttributes | null | undefined = useSelector(
    (state: RootState) => state.chargers.selectedCharger
  );

  // ────────────────────────────────────────────────────────────────────────────────
  // Local state unrelated to Stripe
  // ────────────────────────────────────────────────────────────────────────────────
  const [email, setEmail] = useState<string | null>(null);
  const [guestEmail, setGuestEmail] = useState<string | null>(null);
  const [storedEmail, setStoredEmail] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [showCreateAccount, setShowCreateAccount] = useState<boolean>(false);

  const [myDistanceFromCharger, setMyDistanceFromCharger] =
    useState<string>('');
  const [myTimeFromCharger, setMyTimeFromCharger] = useState<string>('');

  const [myLocation, setMyLocation] =
    useState<Location.LocationObjectCoords | null>(
      route?.params.myLocation ?? null
    );

  // Schedule‐related
  const defaultStartTime = new Date();
  const defaultEndTime = new Date(
    defaultStartTime.getTime() + 12 * 60 * 60 * 1000
  );
  const [scheduleStartTime, setScheduleStartTime] =
    useState<Date>(defaultStartTime);
  const [scheduleEndTime, setScheduleEndTime] = useState<Date>(defaultEndTime);
  const [selectedMaxKwh, setSelectedMaxKwh] = useState<number | null>(null);
    const [scheduleSet, setScheduleSet] = useState<boolean>(false);

  // Tooltip
  const [showFeeTooltipVisible, setShowFeeTooltipVisible] =
    useState<boolean>(false);

  // Refs
  const scrollViewRef = useRef<any>(null);
  const bottomSheetModalRef = useRef<BottomSheetModalRef>(null);
  // True once the current order has been successfully checked out. A
  // checked-out order (and its schedule) must never be cancelled by the
  // navigate-away cleanup below — cancelling it deletes the user's scheduled
  // charge on the backend.
  const orderCheckedOutRef = useRef<boolean>(false);

  const renderCharger = useMemo(() => selectedCharger, [selectedCharger]);
  const isFreeOverride = isDiscountEffectivelyFree(renderCharger?.user_pricing_override);

  // ────────────────────────────────────────────────────────────────────────────────
  // Delegate Stripe logic by platform:
  // ────────────────────────────────────────────────────────────────────────────────
  // onNewOrderCreated will let us keep track of the current Order on the screen
  const [webOrder, setWebOrder] = useState<OrderAttributes | null>(null);
  const onWebOrderCreated = (o: OrderAttributes) => {
    orderCheckedOutRef.current = false;
    setWebOrder(o);
  };

  const {
    order: webStripeOrder,
    paymentData,
    paymentsDropdownOpen,
    loadError,
    chargerInUse,
    chargerUnavailableMessage,
    onDropdownPressed,
    onPaymentSubmit,
    handleRemovePayment,
    stripeElements,
    reinitializeForNewOrder,
  } = useStripeWeb({
    selectedCharger: renderCharger,
    onNewOrderCreated: onWebOrderCreated,
  });

  const [mobileOrder, setMobileOrder] = useState<OrderAttributes | null>(null);
  const onMobileOrderCreated = (o: OrderAttributes) => {
    orderCheckedOutRef.current = false;
    setMobileOrder(o);
  };

  // Which "order" we expose to the rest of the hook depends on platform
  const order = Platform.OS === 'web' ? webStripeOrder : mobileOrder;

  // ────────────────────────────────────────────────────────────────────────────────
  // Effects: Initialization
  // ────────────────────────────────────────────────────────────────────────────────
  // 1) Grab email from AsyncStorage & load driver plan & get distance/time
  useEffect(() => {
    const getStorageInfo = async () => {
      const userEmail = await AsyncStorage.getItem('email');
      setStoredEmail(userEmail);
    };
    const fetchDriverPlan = async () => {
      dispatch(getDriverPlan());
    };

    // Request location permissions & user location
    const requestLocation = async () => {
      const uloc = await getUserLocation();
      setMyLocation(uloc);
    };

    // Distance/time fetchers
    const fetchDistance = async (loc: Location.LocationObjectCoords) => {
      if (renderCharger) {
        const dist = getMyDistanceFromCharger(loc, renderCharger);
        setMyDistanceFromCharger(dist.toString());
      }
    };
    const fetchDrivingTime = async (loc: Location.LocationObjectCoords) => {
      if (renderCharger?.uuid) {
        const t = await getMyDrivingTime(loc, renderCharger.uuid);
        setMyTimeFromCharger(t);
      }
    };

    requestLocation();
    getStorageInfo();
    fetchDriverPlan();

    // Fetch full charger details (the list endpoint may omit user_pricing_override)
    if (selectedCharger?.uuid) {
      dispatch(updateSelectedCharger(selectedCharger.uuid));
    }

    if (route?.params.searchCharger === undefined) {
      // Pre-filled distance/time from navigation params
      setMyDistanceFromCharger(route?.params.myDistanceFromCharger!);
      setMyTimeFromCharger(route?.params.myTimeFromCharger!);

      if (!route?.params.myDistanceFromCharger) {
        // We need to recalc
        fetchDistance(myLocation!);
      }
      if (!route?.params.myTimeFromCharger) {
        fetchDrivingTime(myLocation!);
      }
    }

    if (session) {
      setEmail(session.email!);
    }
  }, []);

  // 2) Whenever session changes, re-grab stored email
  useEffect(() => {
    const syncStoredEmail = async () => {
      const userEmail = await AsyncStorage.getItem('email');
      setStoredEmail(userEmail);
      if (userEmail) setEmail(userEmail);
    };
    syncStoredEmail();
  }, [session]);

  // 3) Ensure fresh state when returning to screen
  useFocusEffect(
    React.useCallback(() => {
      // If there's no order when focusing, ensure we start with clean state
      // This handles the case where user returns after the cleanup has run
      if (!order) {
        // Reset schedule state to defaults
        const defaultStartTime = new Date();
        const defaultEndTime = new Date(defaultStartTime.getTime() + 12 * 60 * 60 * 1000);
        setScheduleStartTime(defaultStartTime);
        setScheduleEndTime(defaultEndTime);
        setSelectedMaxKwh(null);
        setScheduleSet(false);

        // Reset form state
        setGuestEmail(null);
        setSelectedOption('');
        setShowCreateAccount(false);
        setShowFeeTooltipVisible(false);
      }
    }, [order])
  );

  // 4) When navigating away, cancel order and reset schedule state
  useEffect(() => {
    const unsubscribe = navigation.addListener('blur', async () => {
      const navState = navigation.getState();
      const nextRoute = navState.routes[navState.index];
      const nextName = nextRoute?.name;

      // Only maintain state when navigating to ChargingScreen (to preserve order during charging)
      if (nextName !== 'ChargerStation' && nextName !== 'ChargingScreen') {
        // Cancel any existing order (with payment intent) — but only if it was
        // never checked out. Cancelling a checked-out order deletes the user's
        // scheduled charge on the backend.
        const orderIsActiveCharge =
          !!order &&
          !!activeCharges?.some((charge) => charge.order?.id === order.id);

        if (order && !orderCheckedOutRef.current && !orderIsActiveCharge) {
          try {
            if (Platform.OS === 'web') {
              await cancelOrder(webStripeOrder!.id);
            } else {
              await cancelOrder(mobileOrder!.id);
            }
          } catch (error) {
            console.error('Error canceling order during navigation:', error);
          }
        }

        // Reset schedule state
        const defaultStartTime = new Date();
        const defaultEndTime = new Date(defaultStartTime.getTime() + 12 * 60 * 60 * 1000);
        setScheduleStartTime(defaultStartTime);
        setScheduleEndTime(defaultEndTime);
        setSelectedMaxKwh(null);
        setScheduleSet(false);

        // Reset order state
        if (Platform.OS === 'web') {
          setWebOrder(null);
        } else {
          setMobileOrder(null);
        }

        // Reset other form state
        setGuestEmail(null);
        setSelectedOption('');
        setShowCreateAccount(false);
        setShowFeeTooltipVisible(false);
      }
    });
    return () => unsubscribe();
  }, [navigation, route?.name, order, webStripeOrder, mobileOrder, activeCharges]);

  // 5) Auto-scroll when "Create Account" appears
  useEffect(() => {
    if (showCreateAccount) {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }
  }, [showCreateAccount]);



  // ────────────────────────────────────────────────────────────────────────────────
  // Handlers exposed by this hook
  // ────────────────────────────────────────────────────────────────────────────────
  // Email + Create/Sign-in UI
  const validateEmail = (e: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(e);
  };

  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
  const handleEmailChange = React.useCallback((e: string) => {
    setGuestEmail(e);
    if (e.length > 0) setIsValidEmail(validateEmail(e));
    else setIsValidEmail(false);
  }, []);

  const handleOptionSelect = React.useCallback((val: string) => {
    setSelectedOption((p) => (p === val ? '' : val));
  }, []);
  
  const handleCreateAccount = React.useCallback(() => {
    setShowCreateAccount((p) => !p);
  }, []);
  
  const handleSignIn = React.useCallback(() => {
    bottomSheetModalRef.current?.present();
  }, []);

  // Schedule setters
  const setScheduleTimes = (
    start: Date,
    end?: Date | null,
    maxKwh?: number | null
  ) => {
    const now = new Date();
    if (start < now) setScheduleStartTime(now);
    else setScheduleStartTime(start);

    if (end) setScheduleEndTime(end);
    else setScheduleEndTime(new Date(start.getTime() + 12 * 60 * 60 * 1000));

    setSelectedMaxKwh(maxKwh ?? null);
    setScheduleSet(true);
  };
  const clearSchedule = () => {
    // Reset the START as well. Previously only scheduleSet/end/maxKwh were
    // reset, so cancelling the Scheduler after confirming a time left the
    // confirmed start in state: the UI showed "Set Schedule" (no schedule)
    // while checkout still sent that future start to Emporia — sessions
    // silently began at the stale time (e.g. next midnight).
    const now = new Date();
    setScheduleSet(false);
    setScheduleStartTime(now);
    setScheduleEndTime(new Date(now.getTime() + 12 * 60 * 60 * 1000));
    setSelectedMaxKwh(null);
  };

  // "Complete Checkout" logic (shared by web/mobile)
  const [checkoutButtonDisabled, setCheckoutButtonDisabled] = useState<boolean>(
    Platform.OS === 'web'
  );

  useEffect(() => {
    if (Platform.OS === 'web') {
      const shouldBeDisabled = isFreeOverride
        ? (RoleService.isGuest(session) && !isValidEmail)
        : (!paymentData || (RoleService.isGuest(session) && !isValidEmail));
      setCheckoutButtonDisabled(shouldBeDisabled);
    }
  }, [paymentData, session?.role, isValidEmail, isFreeOverride]);

  const handleCheckoutButton = async (): Promise<void> => {
    if (Platform.OS === 'web') {
      if (checkoutButtonDisabled) {
        return;
      }
      setCheckoutButtonDisabled(true);
    }

    if (!session) {
      notificationService.sendNotification('error', 'Invalid Session');
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }
    const currentEmail = RoleService.isGuest(session) ? guestEmail : session.email!;
    if (!currentEmail) {
      notificationService.sendNotification('error', 'Please enter your email');
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }
    if (!order) {
      notificationService.sendNotification(
        'error',
        'Problem creating your order'
      );
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }

    const liveOrder = currentOrderRef.current ?? order; // prefer ref
    if (!liveOrder) {
      notificationService.sendNotification('error', 'Problem creating your order');
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }

    // Check unavailable times before proceeding
    if (selectedCharger && isCurrentlyUnavailable(selectedCharger.unavailable_times ?? null)) {
      notificationService.sendNotification(
        'error',
        'This charger is currently in an unavailable period. Please try again later.'
      );
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }

    // Declared outside the try so the catch block can clean up a schedule
    // that was created before checkout failed.
    let scheduleObj: any = null;

    try {
      // 1) Update order email
      await updateOrderEmail(order.id, currentEmail);

      // 2) Create schedule if applicable.
      //
      // "Charge now" on an OCPP charger must NOT go through /schedule/set:
      // OCPP supports an immediate RemoteStart, and a ReserveNow is rejected by
      // the charger whenever the connector is in use (exactly the "charge now"
      // state). So for an immediate window on an OCPP charger we skip scheduling
      // entirely and let checkout do the RemoteStart (no scheduleId).
      //
      // Emporia has no RemoteStart and must always schedule. Future windows on
      // either manufacturer still schedule (ReserveNow for OCPP 2.0.1).
      if (selectedCharger?.can_schedule && selectedCharger?.uuid) {
        const isOcpp = isOcppCharger(selectedCharger);

        // "Charge now" = the user never confirmed a schedule. Anchor the
        // window to the moment of checkout rather than to scheduleStartTime,
        // which is captured when the screen mounts and may be minutes stale
        // (or, before clearSchedule reset it, a leftover picker value).
        const chargeNow = !scheduleSet;
        const windowStart = chargeNow ? new Date() : scheduleStartTime;
        const windowEnd = chargeNow
          ? new Date(windowStart.getTime() + 12 * 60 * 60 * 1000)
          : scheduleEndTime;

        // A window is "immediate" when its start is within ~60s of now
        // (matches the backend's immediate-window detection).
        const isImmediate =
          chargeNow || windowStart.getTime() <= Date.now() + 60 * 1000;
        const skipScheduling = isOcpp && isImmediate;

        if (!skipScheduling) {
          const payload: SendSchedule = {
            start_time: windowStart.toISOString(),
            end_time: windowEnd.toISOString(),
            chargerId: selectedCharger.uuid,
            orderId: order.id,
            max_kwh: chargeNow ? null : selectedMaxKwh,
            // Lets the backend anchor Emporia "charge now" windows to its own
            // clock instead of trusting the client timestamp.
            immediate: isImmediate,
          };
          const res = await setSchedule(payload);
          if (Array.isArray(res)) scheduleObj = res[0];
          else scheduleObj = res;

          if (scheduleObj && scheduleObj.immediate) {
            // Safety net: backend says this OCPP charger can charge now, so
            // proceed to checkout without a schedule. setSchedule already
            // informed the user.
            scheduleObj = null;
          } else if (!scheduleObj?.scheduleId) {
            // Scheduling failed (e.g. 409 — charger busy for a future window,
            // or a network error). Success is determined by the presence of a
            // scheduleId — NOT by the absence of a `message` field, which the
            // backend may also include on success. Keying off `message` here
            // previously made a successful schedule abort checkout silently,
            // leaving an orphaned schedule for the backend's stale-order
            // sweeper to delete minutes later.
            //
            // setSchedule already surfaced the specific reason. The order stays
            // AUTHORIZED, so the user can retry with the same order (pick another
            // time or charge now) without re-paying. Just abort checkout here.
            if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
            return;
          }
        }
      }

      // 3) Perform checkout with retry — the Stripe payment authorization
      // webhook may not have reached the backend yet when this fires
      // immediately after the payment sheet closes on mobile.
      const MAX_CHECKOUT_RETRIES = 4;
      const RETRY_DELAY_MS = 2000;
      let lastResp: any = null;

      for (let attempt = 1; attempt <= MAX_CHECKOUT_RETRIES; attempt++) {
        lastResp = await checkoutCharger(
          liveOrder.id,
          currentEmail,
          scheduleObj?.scheduleId ?? null
        );

        if (isSuccessful(lastResp.status)) {
          // Mark the order as checked out so the navigate-away cleanup never
          // cancels it (which would delete any schedule tied to it).
          orderCheckedOutRef.current = true;
          if (selectedCharger?.uuid) {
            dispatch(updateSelectedCharger(selectedCharger.uuid));
          }
          navigation.navigate('ChargingScreen', { order, scheduleObj });
          return;
        }

        const msg = ((lastResp.payload as any)?.message ?? '').toLowerCase();
        const isAuthDelay = msg.includes('not authorized') || msg.includes('not yet authorized');
        if (!isAuthDelay || attempt === MAX_CHECKOUT_RETRIES) break;

        console.log(`[useChargingStation] Checkout attempt ${attempt} got "${msg}", retrying in ${RETRY_DELAY_MS}ms...`);
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }

      // Checkout failed for good. Remove the schedule we just created so it
      // isn't left orphaned on a never-checked-out order — the backend's
      // stale-order sweeper would otherwise cancel the order later and delete
      // the schedule invisibly. Retrying checkout recreates the schedule.
      if (scheduleObj?.scheduleId) {
        await deleteSchedule(scheduleObj.scheduleId, { silent: true });
        scheduleObj = null;
      }

      const errorMessage = (lastResp?.payload as any)?.message || 'Error checking out charger.';
      notificationService.sendNotification('error', errorMessage, errorMessage);
    } catch (e: any) {
      console.error(e);
      // Same orphan cleanup as above for the exception path.
      if (scheduleObj?.scheduleId) {
        await deleteSchedule(scheduleObj.scheduleId, { silent: true });
        scheduleObj = null;
      }
      notificationService.sendNotification(
        'error',
        `Error checking out: ${e instanceof Error ? e.message : String(e)}`
      );
    }
        if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Tooltip handlers
  // ────────────────────────────────────────────────────────────────────────────────
  const showFeeTooltip = () => setShowFeeTooltipVisible(true);
  const hideFeeTooltip = () => setShowFeeTooltipVisible(false);

  // ────────────────────────────────────────────────────────────────────────────────
  // Mobile Stripe hook (after handleCheckoutButton is defined)
  // ────────────────────────────────────────────────────────────────────────────────
  const mobileStripeResult =
    Platform.OS === 'web'
      ? { 
        openPaymentSheet: async () => {}, 
        isPaymentSheetInitialized: true, 
        reinitializePaymentSheet: async () => {},
        chargerInUse: false,
        chargerUnavailableMessage: null as string | null,
      }
      : useStripeMobile({
          selectedCharger: renderCharger,
          onNewOrderCreated: onMobileOrderCreated,
          onPaymentSuccess: handleCheckoutButton,
        });

  const { openPaymentSheet, isPaymentSheetInitialized, reinitializePaymentSheet, chargerInUse: mobileChargerInUse, chargerUnavailableMessage: mobileChargerUnavailableMessage } = mobileStripeResult;
  
  const currentOrderRef = useRef<OrderAttributes | null>(null);
  useEffect(() => {
    currentOrderRef.current = Platform.OS === 'web' ? webStripeOrder : mobileOrder;
  }, [webStripeOrder, mobileOrder]);

  const mobileReinitRef = useRef(reinitializePaymentSheet);
  useEffect(() => { mobileReinitRef.current = reinitializePaymentSheet; }, [reinitializePaymentSheet]);

  const lastInitForOrderId = useRef<string | null>(null);

  const setNewOrder = React.useCallback(async (next: OrderAttributes | null) => {
    if (!next) return;

    orderCheckedOutRef.current = false;
  
    if (Platform.OS === 'web') {
      setWebOrder(next);
      currentOrderRef.current = next;
      reinitializeForNewOrder?.(next);
    } else {
      setMobileOrder(next);
      currentOrderRef.current = next;
  
      if (lastInitForOrderId.current !== next.id) {
        lastInitForOrderId.current = next.id;
        // PASS THE ORDER so mobile hook does not create another one
        await mobileReinitRef.current?.(next);
      }
    }
  }, [reinitializeForNewOrder]);
  
  // ────────────────────────────────────────────────────────────────────────────────
  // Payment sheet auto-recovery effect (after mobile hook is declared)
  // ────────────────────────────────────────────────────────────────────────────────
  // useEffect(() => {
  //   // If we're on mobile and have an order but payment sheet is not initialized, try to reinitialize
  //   if (Platform.OS !== 'web' && order && !isPaymentSheetInitialized) {
  //     //console.log('Detected uninitialized payment sheet with active order, attempting recovery...');
  //     const timer = setTimeout(() => {
  //       reinitializePaymentSheet();
  //     }, 1000); // Small delay to avoid rapid re-initialization
      
  //     return () => clearTimeout(timer);
  //   }
  // }, [newOrder, isPaymentSheetInitialized, reinitializePaymentSheet]);

  // ────────────────────────────────────────────────────────────────────────────────
  // Refresh charger data (for pull-to-refresh)
  // ────────────────────────────────────────────────────────────────────────────────
  const refreshChargerData = React.useCallback(async () => {
    if (!selectedCharger?.uuid) return;
    
    try {
      // Re-fetch the charger data to get updated status (including OCPP status)
      await dispatch(updateSelectedCharger(selectedCharger.uuid));
    } catch (error) {
      console.error('Error refreshing charger data:', error);
      notificationService.sendNotification(
        'error',
        'Failed to refresh charger data'
      );
    }
  }, [dispatch, selectedCharger?.uuid]);

  // ────────────────────────────────────────────────────────────────────────────────
  // Expose everything to the screen
  // ────────────────────────────────────────────────────────────────────────────────
  return {
    // Common state
    email,
    guestEmail,
    storedEmail,
    selectedOption,
    showCreateAccount,
    myDistanceFromCharger,
    myTimeFromCharger,
    myLocation,

    // Orders + payments
    order,
    setNewOrder,
    paymentData: Platform.OS === 'web' ? paymentData : null,
    paymentsDropdownOpen: Platform.OS === 'web' ? paymentsDropdownOpen : false,
    loadError: Platform.OS === 'web' ? loadError : false,
    chargerInUse: Platform.OS === 'web' ? chargerInUse : mobileChargerInUse,
    chargerUnavailableMessage: Platform.OS === 'web' ? chargerUnavailableMessage : mobileChargerUnavailableMessage,
    stripeElements: Platform.OS === 'web' ? stripeElements : undefined,

    // Stripe handlers (web)
    onDropdownPressed: Platform.OS === 'web' ? onDropdownPressed : () => {},
    onPaymentSubmit: Platform.OS === 'web' ? onPaymentSubmit : async () => {},
    handleRemovePayment: Platform.OS === 'web' ? handleRemovePayment : () => {},

    // Stripe handlers (mobile)
    openPaymentSheet: Platform.OS !== 'web' ? openPaymentSheet : async () => {},
    isPaymentSheetInitialized: Platform.OS !== 'web' ? isPaymentSheetInitialized : true,
    reinitializePaymentSheet: Platform.OS !== 'web' ? reinitializePaymentSheet : async () => {},

    // Checkout
    checkoutButtonDisabled,
    handleCheckoutButton,

    // Schedule
    scheduleStartTime,
    scheduleEndTime,
    selectedMaxKwh,
    scheduleSet,
    setScheduleTimes,
    clearSchedule,

    // Tooltip
    showFeeTooltipVisible,
    showFeeTooltip,
    hideFeeTooltip,

    // Other UI state
    isValidEmail,
    activeCharges,
    session,
    selectedCharger,

    // Refs
    scrollViewRef,
    bottomSheetModalRef,

    // Shared UI handlers
    handleEmailChange,
    handleOptionSelect,
    handleCreateAccount,
    handleSignIn,

    // Free override
    isFreeOverride,

    // Refresh
    refreshChargerData,
  };
}
